import type { ScaleTime } from 'd3-scale';

export const MAX_DATA_POINTS_DISTANCE = 1_000_000;

/**
 * Given a timescale, return a new scale with the "clamp" option set to true.
 *
 * @param timescale - the timescale to clamp.
 */
export function getClampedTimeScale(
  timescale: ScaleTime<number, number>,
): ScaleTime<number, number> {
  return timescale.copy().clamp(true);
}

/**
 * Given a timestamp, a timescale and a clamped timescale, returns the number coordinate where the timestamp should be positioned.
 * When the timescale and clamped timescale have the same value for a timestamp, that value is returned.
 * When the timescale and clamped timescale have different values for a timestamp, the value of the clamped scale is returned as far as it doesn't exceed the
 * {@link MAX_DATA_POINTS_DISTANCE}, in which case that distance is returned as the value.
 *
 * Context: When there's a huge distance between data points, the lines and area in canvas breaks in some cases (See ADES-3545)
 *
 * @param timestamp -
 * @param timeScale -
 * @param clampedTimeScale -
 */
export function getValueWithBoundaries(
  timestamp: Date | number,
  timeScale: ScaleTime<number, number>,
  clampedTimeScale: ScaleTime<number, number>,
) {
  const value = timeScale(timestamp) ?? 0;
  const clampedValue = clampedTimeScale(timestamp) ?? 0;
  const isClamped = clampedValue !== value;

  if (isClamped) {
    return clampedValue > value
      ? Math.max(-MAX_DATA_POINTS_DISTANCE, value)
      : Math.min(value, clampedValue + MAX_DATA_POINTS_DISTANCE);
  }

  return value;
}
